<template>
  <div :class="['filter-control', { 'min-width': minWidth }]">
    <Select
      v-if="isSelect"
      v-model="inputValue"
      :options="options.options"
      :placeholder="options.placeholder || options.name"
      :label="options.label || options.placeholder || options.name"
      :multiple="options.multiple"
      :search="options.search"
      :class="options.class"
      class="mb-0 select"
      :name="options.name"
      :auto-width="options.autoWidth == false ? false : true"
      :no-label="noLabel"
      :body-render="bodyRender"
    >
      <template #item-row="{ option }">
        <div class="option-row">
          <TruncateText
            :class="{ 'strike-through': option.archived }"
            :text="option.label"
          />
          <div v-if="option.sublabel" class="option-row__sublabel">
            {{ option.sublabel }}
          </div>
        </div>
      </template>
    </Select>
    <Datepicker
      v-if="isDate"
      v-model="inputValue"
      placeholder="Search"
      class="mb-0 report-date-input"
      :name="options.name"
      :label="options.label"
      :validation-errors="v$.$errors"
      :no-label="noLabel"
    />
    <Datepicker
      v-if="isDateRange"
      v-model="inputValue"
      range
      placeholder="Search"
      class="mb-0 report-date-input"
      shortcuts
      :periods="options.options"
      :name="options.name"
      :label="options.label"
      :validation-errors="v$.$errors"
      :no-label="noLabel"
    />
    <ToggleSwitch
      v-if="isToggle"
      v-model="inputValue"
      class="mb-0"
      :label-off="options.options[0]"
      :label-on="options.options[1]"
      :name="options.name"
      :validation-errors="v$.$errors"
      :no-label="noLabel"
      data-cy="reportToggle"
    ></ToggleSwitch>
    <LineItemSelect
      v-if="isLineItemSelect"
      v-model="inputValue"
      :items="options.items"
      :render-limit="options.renderLimit"
      :class="options.class"
      class="mb-0 select line-item-select"
      :no-label="noLabel"
    />
    <WorkWeekFilter v-if="isWorkWeekFilter" v-model="inputValue" />
    <div v-else></div>
  </div>
</template>

<script>
import moment from "moment-timezone";
import { EventBus } from "@/lib/event_bus.js";
import { isFalsy } from "@/lib/helpers.js";

import Datepicker from "@/components/Datepicker.vue";
import ToggleSwitch from "@/components/ToggleSwitch.vue";
import TruncateText from "@/components/TruncateText.vue";
import CONSTANTS from "@/Constants.js";
import LineItemSelect from "@/Reports/LineItemSelect.vue";
import WorkWeekFilter from "@/Reports/WorkWeekFilter.vue";
import useVuelidate from "@vuelidate/core";
import { requiredIf } from "@vuelidate/validators";

export default {
  components: {
    Datepicker,
    ToggleSwitch,
    TruncateText,
    LineItemSelect,
    WorkWeekFilter
  },
  props: {
    options: {
      type: Object,
      default: () => ({})
    },
    modelValue: {
      type: [String, Number, Array, Boolean],
      default: null
    },
    noLabel: {
      type: Boolean,
      default: false
    },
    // Adds a minimum width to inputs
    minWidth: {
      type: Boolean,
      default: true
    },
    bodyRender: {
      type: Boolean,
      default: true
    },
    isRequired: {
      type: Boolean,
      default: false
    }
  },
  emits: ["update:modelValue"],
  setup(props) {
    return {
      v$: useVuelidate()
    };
  },
  validations() {
    return {
      inputValue: {
        requiredIf: requiredIf(this.isRequired),
        $autoDirty: true
      }
    };
  },
  data() {
    return {
      initialValue: this.modelValue
    };
  },
  computed: {
    isDate() {
      return this.options.type === CONSTANTS.REPORT_FILTER_TYPES.date;
    },
    isDateRange() {
      return this.options.type === CONSTANTS.REPORT_FILTER_TYPES.dateRange;
    },
    isSelect() {
      return this.options.type === CONSTANTS.REPORT_FILTER_TYPES.select;
    },
    isMultiSelect() {
      return this.isSelect && this.options.multiple;
    },
    isToggle() {
      return this.options.type === CONSTANTS.REPORT_FILTER_TYPES.toggle;
    },
    isLineItemSelect() {
      return this.options.type === CONSTANTS.REPORT_FILTER_TYPES.lineItemSelect;
    },
    isWorkWeekFilter() {
      return this.options.type === CONSTANTS.REPORT_FILTER_TYPES.workWeekFilter;
    },
    inputValue: {
      get() {
        return this.transformValue(this.modelValue);
      },
      set(val) {
        if (this.isSelect && this.options.all) {
          const allValue = this.options.all;

          val ??= [];

          let beingDeselected = [];

          if (typeof this.inputValue === "object" && this.inputValue != null) {
            beingDeselected =
              this.inputValue.filter((x) => !val.includes(x)) ?? [];
          }

          if (beingDeselected.length > 0) {
            // deselecting
            if (beingDeselected.includes(allValue)) {
              // deselecting all, select none
              val = [];
            } else {
              // remove "*" as not all are selected
              val = val.filter((item) => item !== allValue);
            }
          } else {
            // selecting
            // "*" is selected => all options should be set.
            if (val?.includes(allValue)) {
              val = this.options.options.map((opt) => opt.value);
            }
          }
        }

        this.handleValueChange(val);
      }
    }
  },
  mounted() {
    EventBus.$on("clear-datatable-filters", this.clearFilter);
    EventBus.$on("clear-filter", this.clearFilterCheck);
  },
  beforeUnmount() {
    EventBus.$off("clear-datatable-filters", this.clearFilter);
    EventBus.$off("clear-filter", this.clearFilterCheck);
  },
  methods: {
    clearFilterCheck(filter) {
      if (filter.name === this.options.name) {
        this.clearFilter();
      }
    },
    clearFilter() {
      // this.inputValue = this.transformValue(this.initialValue) || null;
      this.inputValue = null;
    },
    formatDate(date) {
      return moment(date).format(CONSTANTS.DATE_API_FORMAT);
    },
    getDateRange(range) {
      return range
        .map((date) => moment(date).format(CONSTANTS.DATE_API_FORMAT))
        .join("-");
    },
    handleValueChange(value) {
      if (this.inputValue !== value) {
        // Format date range for url param
        if (value && this.isDateRange) {
          // handle cleared date range
          value =
            !value[0] && !value[1] ? null : this.getDateRange(value || []);
        }
        // Format date for url param
        if (value && this.isDate) {
          value = this.formatDate(value);
        }
        // format array as string
        if (value && this.options.multiple) {
          value = Array.isArray(value) ? value.join(",") : value;
        }
        this.$emit("update:modelValue", value);
      }
    },
    transformValue(initialValue) {
      // Transform input value from api format to v-model format for
      // the correct input type
      let value = initialValue;
      if (!isFalsy(value) && this.options.value_type === "number") {
        value = parseInt(value, 10);
      }
      if (value && (this.isDateRange || this.isWorkWeekFilter)) {
        value = value.split("-");
      }
      if (this.isToggle) {
        value = typeof value === "string" ? Boolean(value) : value;
      }
      // Convert query string to array
      if (
        (this.isMultiSelect || this.isLineItemSelect) &&
        value &&
        !Array.isArray(value)
      ) {
        value = String(value).split(",");
      }
      return value;
    }
  }
};
</script>

<style lang="scss" scoped>
/*
.report-date-input {
  width: 185px !important;
}
table .report-date-input {
  width: 100% !important;
  min-width: 185px;
}*/
.filter-control.min-width {
  min-width: 185px;
}
.line-item-select {
  position: relative;
  z-index: 1;
}
::v-deep(.line-item-select .select-field-label) {
  display: none;
}
.option-row__sublabel {
  font-size: 12px;
  font-weight: normal;
  line-height: 1.2;
  opacity: 75%;
}
</style>
